import React, { useEffect, useState, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import axiosInstance from "utils/axios";

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

function Logs() {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [realTimeLogs, setRealTimeLogs] = useState([]);
  const [historicalLogs, setHistoricalLogs] = useState([]);
  const [combinedLogs, setCombinedLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState(3);
  const [levelFilter, setLevelFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const eventSourceRef = useRef(null);
  const logContainerRef = useRef(null);
  const autoScrollRef = useRef(true);
  const isInitialLoad = useRef(true);  // Track if this is the first page load

  // Load historical logs
  const loadHistoricalLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        days: daysFilter.toString(),
        limit: "5000"
      });
      
      if (levelFilter) params.append("level", levelFilter);
      if (accountFilter) params.append("account_name", accountFilter);
      
      const response = await axiosInstance.get(`/api/v1/alerts/logs/history?${params}`);
      setHistoricalLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to load historical logs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Combine historical and real-time logs
  useEffect(() => {
    const combined = [...historicalLogs, ...realTimeLogs];
    // Remove duplicates based on timestamp and message
    const uniqueLogs = combined.filter((log, index, self) => 
      index === self.findIndex(l => 
        l.timestamp === log.timestamp && l.message === log.message
      )
    );
    
    // Sort by timestamp (oldest first - so latest logs appear at bottom)
    uniqueLogs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    setCombinedLogs(uniqueLogs.slice(-5000)); // Keep only latest 5000 (from the end)
    
    // Auto-scroll to bottom when new logs are added OR on initial load
    setTimeout(() => {
      const container = logContainerRef.current;
      if (container) {
        // Always scroll to bottom on initial load (when user first opens logs page)
        if (isInitialLoad.current) {
          container.scrollTop = container.scrollHeight;
          autoScrollRef.current = true;
          setAutoScrollEnabled(true);
          isInitialLoad.current = false; // Mark initial load as complete
          console.log("📍 Initial scroll to bottom completed");
        } 
        // On subsequent updates, only scroll if auto-scroll is enabled
        else if (autoScrollRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }, 150); // Slightly increased delay to ensure DOM is fully updated
  }, [historicalLogs, realTimeLogs]);

  // Auto-scroll when new logs are added (separate effect for better control)
  useEffect(() => {
    if (combinedLogs.length > 0 && autoScrollRef.current) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        const container = logContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [combinedLogs.length]); // Only trigger when the number of logs changes

  // Load historical logs when filters change
  useEffect(() => {
    loadHistoricalLogs();
    // Reset auto-scroll to enabled when filters change (user expects to see latest filtered results)
    if (!isInitialLoad.current) { // Don't trigger on initial load
      autoScrollRef.current = true;
      setAutoScrollEnabled(true);
      console.log("🔄 Filters changed - auto-scroll reset to enabled");
    }
  }, [daysFilter, levelFilter, accountFilter]);

  // Real-time logs via SSE with token authentication
  useEffect(() => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!token) {
      console.error("No access token found for SSE");
      return;
    }
    
    const source = new EventSource(`${BACKEND_BASE_URL}/api/v1/alerts/logs/stream?token=${token}`);
    eventSourceRef.current = source;
    
    console.log("🔌 SSE connection established");
    
    source.onopen = () => {
      console.log("✅ SSE connection opened");
    };

    source.addEventListener("log", (event) => {
      try {
        const log = JSON.parse(event.data);
        console.log("📡 SSE received log:", log);
        setRealTimeLogs((prevLogs) => {
          // Add new log to the end (bottom) of the list
          const updated = [...prevLogs, log];
          return updated.slice(-5000); // Keep only latest 5000 from the end
        });
      } catch (e) {
        console.error("Invalid JSON log:", e);
      }
    });

    // Handle new "connected" event from pub/sub implementation
    source.addEventListener("connected", (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("🎯 SSE connection ready:", data.status);
      } catch (e) {
        console.error("Invalid connected event:", e);
      }
    });

    // Handle new "heartbeat" event from pub/sub implementation  
    source.addEventListener("heartbeat", (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("💓 SSE heartbeat received");
      } catch (e) {
        // Silently ignore heartbeat parsing errors
      }
    });

    source.onerror = (error) => {
      console.error("SSE error:", error);
      source.close();
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        console.log("🔌 SSE connection closed.");
      }
    };
  }, []);

  // Detect if user scrolls up — disable auto-scroll, re-enable when at bottom
  const handleScroll = () => {
    const container = logContainerRef.current;
    if (!container) return;

    // Check if user is at or near the bottom (within 50px for better sensitivity)
    const threshold = 50;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const atBottom = scrollHeight - scrollTop <= clientHeight + threshold;
    
    // Only update if the state actually changed to avoid unnecessary re-renders
    if (autoScrollRef.current !== atBottom) {
      autoScrollRef.current = atBottom;
      setAutoScrollEnabled(atBottom);
      
      // Debug logging for auto-scroll state changes
      if (atBottom) {
        console.log("🔽 Auto-scroll re-enabled (user reached bottom)");
      } else {
        console.log("🔼 Auto-scroll disabled (user scrolled up)");
      }
    }
  };

  const getChipColor = (level) => {
    switch (level) {
      case "ERROR":
        return "error";
      case "WARNING":
        return "warning";
      case "DEBUG":
        return "secondary";
      default:
        return "info";
    }
  };

  const scrollToBottom = () => {
    const container = logContainerRef.current;
    if (container) {
      // Smooth scroll to bottom
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
      
      // Re-enable auto-scroll
      autoScrollRef.current = true;
      setAutoScrollEnabled(true);
      
      console.log("⬇️ Manual scroll to bottom - auto-scroll re-enabled");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} px={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: 2,
                borderRadius: "12px",
                backgroundColor: darkMode 
                  ? theme.palette.background.card 
                  : "white",
              }}
            >
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Logs Viewer (Live)
                </MDTypography>
              </MDBox>

              {/* Filters */}
              <MDBox p={3} pb={2}>
                <MDBox 
                  display="flex" 
                  gap={2} 
                  flexWrap="wrap" 
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    "& .MuiFormControl-root": {
                      minWidth: 140,
                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "#1976d2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                        "&.Mui-focused": {
                          color: "#1976d2",
                        },
                      },
                      "& .MuiSelect-select": {
                        color: darkMode ? "#fff" : "#000",
                        height: "40px !important",
                        display: "flex",
                        alignItems: "center",
                      },
                    },
                    "& .MuiButton-root": {
                      height: 40,
                      minWidth: 100,
                      backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                      borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "#1976d2",
                      color: darkMode ? "#fff" : "#1976d2",
                      "&:hover": {
                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
                        borderColor: darkMode ? "rgba(255, 255, 255, 0.5)" : "#1565c0",
                      },
                      "&:disabled": {
                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.02)" : "#f5f5f5",
                        color: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                >
                  {/* Left side - Filter controls */}
                  {/* <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <FormControl size="small">
                      <InputLabel id="days-label">Days</InputLabel>
                      <Select
                        labelId="days-label"
                        value={daysFilter}
                        label="Days"
                        onChange={(e) => setDaysFilter(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: darkMode ? "#2d2d2d" : "#fff",
                              "& .MuiMenuItem-root": {
                                color: darkMode ? "#fff" : "#000",
                                "&:hover": {
                                  backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: darkMode ? "rgba(25, 118, 210, 0.3)" : "rgba(25, 118, 210, 0.1)",
                                  "&:hover": {
                                    backgroundColor: darkMode ? "rgba(25, 118, 210, 0.4)" : "rgba(25, 118, 210, 0.2)",
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={1}>1 Day</MenuItem>
                        <MenuItem value={3}>3 Days</MenuItem>
                        <MenuItem value={7}>7 Days</MenuItem>
                        <MenuItem value={30}>30 Days</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small">
                      <InputLabel id="level-label" vi>Level</InputLabel>
                      <Select
                        labelId="level-label"
                        value={levelFilter}
                        label="Level"
                        onChange={(e) => setLevelFilter(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: darkMode ? "#2d2d2d" : "#fff",
                              "& .MuiMenuItem-root": {
                                color: darkMode ? "#fff" : "#000",
                                "&:hover": {
                                  backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: darkMode ? "rgba(25, 118, 210, 0.3)" : "rgba(25, 118, 210, 0.1)",
                                  "&:hover": {
                                    backgroundColor: darkMode ? "rgba(25, 118, 210, 0.4)" : "rgba(25, 118, 210, 0.2)",
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="">All Levels</MenuItem>
                        <MenuItem value="INFO">INFO</MenuItem>
                        <MenuItem value="WARNING">WARNING</MenuItem>
                        <MenuItem value="ERROR">ERROR</MenuItem>
                        <MenuItem value="DEBUG">DEBUG</MenuItem>
                      </Select>
                    </FormControl> */}
                    
                    {/* <Button 
                      variant="outlined" 
                      size="small"
                      onClick={loadHistoricalLogs}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Refresh"}
                    </Button>
                  </MDBox> */}

                  {/* Right side - Auto-scroll controls */}
                  <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={scrollToBottom}
                      sx={{
                        backgroundColor: autoScrollEnabled 
                          ? (darkMode ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.05)")
                          : (darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff"),
                        borderColor: autoScrollEnabled 
                          ? (darkMode ? "rgba(76, 175, 80, 0.5)" : "#4caf50")
                          : (darkMode ? "rgba(255, 255, 255, 0.3)" : "#1976d2"),
                        color: autoScrollEnabled 
                          ? (darkMode ? "#4caf50" : "#4caf50")
                          : (darkMode ? "#fff" : "#1976d2"),
                        "&:hover": {
                          backgroundColor: autoScrollEnabled 
                            ? (darkMode ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.1)")
                            : (darkMode ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5"),
                          borderColor: autoScrollEnabled 
                            ? (darkMode ? "rgba(76, 175, 80, 0.7)" : "#45a049")
                            : (darkMode ? "rgba(255, 255, 255, 0.5)" : "#1565c0"),
                        },
                      }}
                    >
                      ↓ Bottom
                    </Button>
                    
                    <Chip
                      label={autoScrollEnabled ? "Auto-scroll ON" : "Auto-scroll OFF"}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 40,
                        borderColor: autoScrollEnabled 
                          ? (darkMode ? "rgba(76, 175, 80, 0.5)" : "#4caf50")
                          : (darkMode ? "rgba(158, 158, 158, 0.5)" : "#9e9e9e"),
                        color: autoScrollEnabled 
                          ? (darkMode ? "#4caf50" : "#4caf50")
                          : (darkMode ? "#9e9e9e" : "#9e9e9e"),
                        backgroundColor: autoScrollEnabled 
                          ? (darkMode ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.05)")
                          : (darkMode ? "rgba(158, 158, 158, 0.05)" : "rgba(158, 158, 158, 0.05)"),
                        "& .MuiChip-label": {
                          fontSize: "12px",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>

              {/* Logs Output */}
              <MDBox
                ref={logContainerRef}
                onScroll={handleScroll}
                p={3}
                pt={0}
                sx={{
                  bgcolor: darkMode 
                    ? theme.palette.background.default 
                    : "#f9fafc",
                  borderRadius: "12px",
                  overflow: "auto",
                  maxHeight: "75vh",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  lineHeight: "1.5",
                }}
              >
                {combinedLogs.length === 0 ? (
                  <MDTypography 
                    variant="body2" 
                    color={darkMode ? "white" : "textSecondary"}
                  >
                    Waiting for logs...
                  </MDTypography>
                ) : (
                  combinedLogs.map((log, idx) => (
                    <MDBox
                      key={idx}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        borderBottom: darkMode 
                          ? "1px solid #444" 
                          : "1px solid #eee",
                        paddingY: "6px",
                        "&:hover": {
                          backgroundColor: darkMode 
                            ? "rgba(255, 255, 255, 0.08)" 
                            : "#eef2f6",
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={log.level}
                          size="small"
                          color={getChipColor(log.level)}
                          variant="filled"
                        />
                        <MDTypography 
                          variant="caption" 
                          color={darkMode ? "white" : "text"} 
                          fontWeight="medium"
                        >
                          {log.timestamp}
                        </MDTypography>
                      </Stack>
                      <MDTypography
                        variant="caption"
                        color={darkMode ? "white" : "text"}
                        sx={{
                          ml: 2,
                          flex: 1,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {log.message}
                      </MDTypography>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Logs;
